import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Route } from "react-router-dom";
import classNames from "classnames";
import MobileDetect from "mobile-detect";
import ApiClient from "../common/utils/apiClient";
import { actions as pageInfoActions } from "../common/pageInfo";
import * as drawerSelectors from "./drawer.selectors";
import * as drawerActions from "./drawer.actions";
import * as eventsActions from "../App/events.actions";
import {
	selectors as bookmakersInfoSelectors,
	actions as bookmakersInfoActions,
} from "../common/bookmakersInfo";
import { actions as modalActions } from "../modals";
import {
	selectors as bookmakersListSelectors,
	actions as bookmakersListActions,
} from "../common/bookmakersList";
import { actions as betActions } from "../common/bet";
import BetPage from "./components/BetPage";
import LoginPage from "./components/LoginPage";
import BetReceiptPage from "./components/BetReceiptPage";
import ForgottenPassword from "./components/ForgottenPasswordPage";
import TransitionRouter from "../common/transitionRouter";
import TopUpPage from "./components/TopUpPage";
import BodyScroll from "./components/BodyScroll";
import ColorSkinWrapper from "../common/colorSkinWrapper";
import { selectors as userInfoSelectors } from "../common/userInfo";
import { getShortGamblingCompliance } from "../common/gamblingCompliance";
import GlobalModal from "../common/globalModal";

import "./styles.scss";

const getIsPhone = () => Boolean(new MobileDetect(navigator.userAgent).phone());

const getIsTablet = () => Boolean(new MobileDetect(navigator.userAgent).tablet());

export class Drawer extends Component {
	constructor(props) {
		super(props);
		this.state = {
			lastTapTime: null,
		};
	}

	componentDidMount() {
		if (!window.location.pathname.includes("/my-bookmakers")) {
			const apiUrls = ApiClient.getApiUrls();
			const isPhone = getIsPhone();
			const isTablet = getIsTablet();
			const loggedInBookmakers = this.getLoggedInBookmakers(isPhone);

			this.props.initializeLoggedInBookmakers(loggedInBookmakers, apiUrls.v1);
			this.props.setDeviceType(isPhone, isTablet);
			this.props.loadAppConfig(isPhone);
			this.props.setApiUrl(apiUrls.v1, apiUrls.v3);

			if (!this.props.selectedBookmaker && !isPhone) {
				this.props.setSelectedBookmaker("#BESTODDS");
			}

			this.props.setInitialized();
		}

		document.addEventListener("keydown", this.handleKeyboardEvent);
	}

	// componentDidUpdate() {
	//     // This feature currently causes 2 issues:
	//     // 1. "Accept and Cancel" via the CTA and the user gets stuck
	//     // 2. The bookmakers list can go off screen and the user cannot select
	//     // bookies if they are not on-screen
	//     const { isDrawerMinimized } = this.props
	//     if (isDrawerMinimized) {
	//         document.body.classList.remove('locked-scrolling')
	//     } else {
	//         document.body.classList.add('locked-scrolling')
	//     }
	// }

	componentWillUnmount() {
		document.removeEventListener("keydown", this.handleKeyboardEvent);
	}

	getLoggedInBookmakers(isPhone) {
		const { bookmakersList } = this.props;
		return bookmakersList.filter(({ availableOnPhone }) => !isPhone || availableOnPhone);
	}

	onMaximizeClick = () => {
		const { isUserLoggedIn, onMaximizeClick, getUserInfo, apiName, feed } = this.props;

		if (isUserLoggedIn) {
			getUserInfo(apiName, feed);
		}

		onMaximizeClick();
	};

	handleKeyboardEvent = (e) => {
		const {
			clearGlobalModal,
			globalModal,
			isQuickslipMode,
			isDrawerMinimized,
			onMinimizeClick,
			resetQuickslip,
			selectedBets,
			resetEmptyBetslip,
		} = this.props;

		const ESC_KEY = 27;
		if (e.keyCode === ESC_KEY) {
			// Global modal...
			if (globalModal !== null) {
				clearGlobalModal();
				return;
			}

			// Quickslip...
			if (!isDrawerMinimized && isQuickslipMode) {
				resetQuickslip();
				return;
			}

			// Betslip...
			if (!isDrawerMinimized && !isQuickslipMode) {
				if (selectedBets.length) {
					onMinimizeClick();
					return;
				}
				resetEmptyBetslip();
			}
		}
	};

	preventZoom = (e) => {
		const currTapTime = e.timeStamp;
		const prevTapTime = this.state.lastTapTime || currTapTime;
		const tapTimeDiff = currTapTime - prevTapTime;
		const isSingleFingerTap = e.touches.length === 1;
		this.setState({ lastTapTime: currTapTime });

		if (!tapTimeDiff || tapTimeDiff > 500 || !isSingleFingerTap) {
			return;
		}
		e.preventDefault();
		e.target.click();
	};

	renderGlobalModal() {
		const { clearGlobalModal, globalModal } = this.props;

		return globalModal !== null ? (
			<GlobalModal
				visible
				onAccept={clearGlobalModal}
				title={globalModal.title}
				body={globalModal.body}
				ctaTitle={globalModal.ctaTitle}
			/>
		) : null;
	}

	render() {
		const {
			selectedBookmakerFeed,
			selectedBets,
			betslipID,
			placedBetDetails,
			isDrawerMinimized,
			onMinimizeClick,
			theme,
			isUserLoggedIn,
			userInfo,
			isQuickslipMode,
			resetEmptyBetslip,
			resetQuickslip,
			feed,
			isBetPlacedSuccessfully,
			clearPlacedBetDetails,
		} = this.props;

		const isEmptyBetslip =
			!isQuickslipMode && selectedBets.length === 0 && theme && theme !== "no-bookmaker";

		const isOpened = !!(
			isEmptyBetslip ||
			selectedBets.length ||
			placedBetDetails.bets.length !== 0
		);

		const shouldShowBetsBadge = isDrawerMinimized;
		const shouldBeFullScreen = !isQuickslipMode;
		const shortGamblingCompliance = getShortGamblingCompliance(feed);

		if (isOpened) {
			return (
				<ColorSkinWrapper bookmakerName={theme}>
					{this.renderGlobalModal()}
					<div
						key='drawer'
						className={classNames(
							"bs-drawer",
							"bs-verticalSlider",
							{ "bs-verticalSlider_up": !isDrawerMinimized },
							{ "bs-verticalSlider_fullscreen": shouldBeFullScreen }
						)}
						onTouchStart={this.preventZoom}
					>
						{shouldShowBetsBadge ? (
							<div className='bs-drawer__badge'>{selectedBets.length}</div>
						) : null}
						<div className='bs-drawer__container'>
							{isQuickslipMode ? shortGamblingCompliance : null}

							<TransitionRouter
								selectedBookmakerFeed={selectedBookmakerFeed}
								bookmakerName={theme}
								betslipID={betslipID}
								selectedBets={selectedBets}
								isUserLoggedIn={isUserLoggedIn}
								userInfo={userInfo}
								toggleBalanceVisibility={this.props.toggleBalanceVisibility}
								isDrawer
								isDrawerMinimized={isDrawerMinimized}
								onMaximizeClick={this.onMaximizeClick}
								onMinimizeClick={onMinimizeClick}
								resetEmptyBetslip={resetEmptyBetslip}
								resetQuickslip={resetQuickslip}
								numberOfSelectedBets={selectedBets.length}
								isQuickslipMode={isQuickslipMode}
								isBetPlacedSuccessfully={isBetPlacedSuccessfully}
								clearPlacedBetDetails={clearPlacedBetDetails}
							>
								<Route exact path='/' component={BetPage} />
								<Route path='/:feed/login' component={LoginPage} />
								<Route path='/bet-receipt' component={BetReceiptPage} />
								<Route
									path='/:feed/forgotten-password'
									component={ForgottenPassword}
								/>
								<Route path='/:feed/top-up' component={TopUpPage} />
							</TransitionRouter>
						</div>
					</div>
					<div
						role='button'
						tabIndex={0}
						className={classNames("bs-overlay", { "bs-hidden": isDrawerMinimized })}
					></div>
					<BodyScroll isDrawerOpen={!isDrawerMinimized} />
				</ColorSkinWrapper>
			);
		}

		return null;
	}
}

const betProps = PropTypes.shape({
	price: PropTypes.string,
	outcomersId: PropTypes.string,
	eventId: PropTypes.string,
	meetingId: PropTypes.string,
	raceId: PropTypes.string,
	raceDate: PropTypes.string,
	horseName: PropTypes.string,
	courseName: PropTypes.string,
	raceTime: PropTypes.string,
	eachWayFraction: PropTypes.string,
	eachWayPlaces: PropTypes.string,
	isBog: PropTypes.string,
});

Drawer.defaultProps = {
	isDrawerMinimized: false,
	selectedBets: [],
	betslipID: null,
	placedBetDetails: {
		freeBet: null,
		bets: [],
	},
	theme: "no-bookie",
	isUserLoggedIn: false,
	globalModal: null,

	apiName: "",
	feed: "",
};

Drawer.propTypes = {
	selectedBookmakerFeed: PropTypes.string.isRequired,
	selectedBets: PropTypes.arrayOf(betProps),
	betslipID: PropTypes.string,
	placedBetDetails: PropTypes.shape({
		freeBet: PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: PropTypes.string,
			amount: PropTypes.number.isRequired,
			minStake: PropTypes.number,
			maxStake: PropTypes.number,
		}),
		bets: PropTypes.arrayOf(
			PropTypes.shape({
				betType: PropTypes.string,
				eachWayFlag: PropTypes.bool,
				receiptId: PropTypes.string,
				stakePrice: PropTypes.number,
				totalStake: PropTypes.number,
				estimatedReturns: PropTypes.number,
				selectedBet: betProps,
			})
		).isRequired,
	}),
	isBetPlacedSuccessfully: PropTypes.bool.isRequired,
	isDrawerMinimized: PropTypes.bool,
	theme: PropTypes.string,
	isUserLoggedIn: PropTypes.bool,
	userInfo: PropTypes.shape({
		name: PropTypes.string,
		balance: PropTypes.number,
		showBalance: PropTypes.bool,
		currencySign: PropTypes.string.isRequired,
	}).isRequired,
	toggleBalanceVisibility: PropTypes.func.isRequired,
	onMaximizeClick: PropTypes.func.isRequired,
	onMinimizeClick: PropTypes.func.isRequired,
	isQuickslipMode: PropTypes.bool.isRequired,
	resetEmptyBetslip: PropTypes.func.isRequired,
	resetQuickslip: PropTypes.func.isRequired,
	clearGlobalModal: PropTypes.func.isRequired,
	globalModal: PropTypes.shape({
		title: PropTypes.string,
		body: PropTypes.string,
		ctaTitle: PropTypes.string,
	}),
	getUserInfo: PropTypes.func.isRequired,
	apiName: PropTypes.string,
	feed: PropTypes.string,
	clearPlacedBetDetails: PropTypes.func.isRequired,
	initializeLoggedInBookmakers: PropTypes.func.isRequired,
	setDeviceType: PropTypes.func.isRequired,
	loadAppConfig: PropTypes.func.isRequired,
	setApiUrl: PropTypes.func.isRequired,
	selectedBookmaker: PropTypes.string,
	setSelectedBookmaker: PropTypes.func.isRequired,
	setInitialized: PropTypes.func.isRequired,
	// eslint-disable-next-line react/forbid-prop-types
	bookmakersList: PropTypes.array,
};

const bookmakerInfoSelector = bookmakersListSelectors.createBookmakerInfoSelector(
	drawerSelectors.drawerBookmakerSelector
);

const userInfoSelector = userInfoSelectors.createUserInfoSelector(
	bookmakersInfoSelectors.selectedBookmakerSelector
);

const userIsLoggedInSelector = bookmakersListSelectors.createIsLoggedInSelector(
	bookmakersInfoSelectors.selectedBookmakerSelector
);

const mapStateToProps = (state, props) => {
	const {
		bookmakersInfo: { selectedBets, courseName, selectedBookmaker, betslipID },
		drawerInfo: { isDrawerMinimized, theme, isQuickslipMode },
		modals: { globalModal },
		bet: { placedBetDetails },
		pageInfo: { apiUrl },
		bookmakersList,
	} = state;

	return {
		...bookmakerInfoSelector(state),
		apiUrl,
		selectedBookmakerFeed: selectedBookmaker,
		selectedBookmaker,
		bookmakersList,
		selectedBets,
		betslipID,
		courseName,
		placedBetDetails,
		isBetPlacedSuccessfully: placedBetDetails.bets.length !== 0,
		isDrawerMinimized,
		theme,
		isUserLoggedIn: userIsLoggedInSelector(state),
		userInfo: userInfoSelector(state, props),
		isQuickslipMode,
		globalModal,
	};
};

const mapDispatchToProps = {
	toggleBalanceVisibility: bookmakersInfoActions.toggleBalanceVisibility,
	onMaximizeClick: drawerActions.maximizeDrawer,
	onMinimizeClick: drawerActions.minimizeDrawer,
	resetEmptyBetslip: drawerActions.resetEmptyBetslip,
	resetQuickslip: drawerActions.resetQuickslip,
	clearGlobalModal: modalActions.clearGlobalModal,
	getUserInfo: bookmakersListActions.getUserInfo,
	clearPlacedBetDetails: betActions.clearPlacedBetDetails,
	initializeLoggedInBookmakers: bookmakersListActions.initializeLoggedInBookmakers,
	setDeviceType: pageInfoActions.setDeviceType,
	loadAppConfig: pageInfoActions.loadAppConfig,
	setApiUrl: pageInfoActions.setApiUrl,
	setSelectedBookmaker: bookmakersInfoActions.setSelectedBookmaker,
	setInitialized: eventsActions.setInitialized,
};

export default connect(mapStateToProps, mapDispatchToProps)(Drawer);



// WEBPACK FOOTER //
// /src/js/drawer/Drawer.jsx